/* ==========================================================================
   Rook Components — Art Calculator (Calculadora do Revendedor)
   Web Component: <rk-art-calculator-element>
   Hidratação 'idle'.

   ── Cálculo ───────────────────────────────────────────────────────────────
   Dadas a largura e a altura da parede (em metros):

     1) nº de faixas    = ceil( largura / largura_da_faixa )
     2) altura da faixa = menor valor da tabela de alturas que seja
                          >= (altura da parede + sangria)
     3) se a altura da faixa >= o limiar "par", o nº de faixas é arredondado
        para cima até ficar par (rolo grande é cortado aos pares)
     4) metros lineares = nº de faixas × altura da faixa, arredondado p/ cima
        (o Shopify só aceita quantidade inteira, então o metro é vendido
        fechado: 16,20 m vira 17 m — na tela e no carrinho, sempre iguais)
        a prazo = metros × preço do metro (= preço da variante)
        à vista = esse total menos o desconto Pix global do tema

   O preço vem do próprio produto (centavos por metro linear) e é publicado
   p/ os blocos rk-price-dominic / rk-pix-dominic, que recalculam sobre os
   mesmos metros. Nada disso se move enquanto o cliente digita: o preço só
   muda no clique em "Calcular".

   ── Carrinho ──────────────────────────────────────────────────────────────
   Resultado e preço só mudam no clique em "Calcular". Quem preenche e vai
   direto ao "adicionar ao carrinho" sem clicar no botão continua protegido
   pelo onFormSubmit, que recalcula em fase de captura antes de o tema ler o
   FormData. A integração:

     - define a quantidade (= METROS lineares) no(s) product-form(s); se o form
       não tiver input[name="quantity"] (bloco de quantidade desativado ou
       escondido), injeta um hidden — é ele que o handleSubmit do tema lê via
       FormData;
     - injeta properties[Largura|Altura|Faixas|Altura da faixa];
     - reforça no submit (captura) no FORM que está sendo enviado — imune a
       múltiplos forms na página e a re-render por troca de variante;
     - bloqueia o submit enquanto as medidas não forem válidas, quando a
       calculadora é a única fonte de quantidade;
     - ignora por completo qualquer product-form dentro de [data-rk-art-ignore]
       (é o caso do botão "Solicitar amostra", que vende outro produto);
     - publica o estado em document.documentElement.dataset.rkFaixas e emite
       rk:faixa:update p/ os blocos de preço reativos.

   Compõe o product-form nativo sem modificá-lo.
   ========================================================================== */

import { RkComponent } from '@rook/component';
import { formatMoney } from '@theme/money-formatting';

const HIDDEN_CLASS = 'rk-art-calc-hidden-qty';

/**
 * Formulários marcados com este atributo ficam fora do alcance da calculadora:
 * nem recebem quantidade/propriedades, nem têm o seletor de quantidade escondido,
 * nem são barrados no submit. É o que isola o botão "Solicitar amostra"
 * (blocks/button-amostra.liquid), que vende OUTRO produto — a amostra — e não o
 * papel de parede por metro linear.
 */
const IGNORE_SELECTOR = '[data-rk-art-ignore]';

/** @param {Element | null | undefined} el */
function isIgnored(el) {
  return !!el?.closest(IGNORE_SELECTOR);
}

/** Tabela padrão de alturas (m), usada se a setting vier vazia. */
const ALTURAS_PADRAO = [
  1.0, 1.2, 1.5, 1.7, 2.0, 2.2, 2.5, 2.7, 3.0, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7,
  3.8, 3.9, 4.0, 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.7, 4.8, 4.9, 5.0, 5.1, 5.2,
  5.3, 5.4, 5.5, 6.0, 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7, 6.8, 6.9, 7.0, 7.1,
  7.2, 7.3, 7.4, 7.5, 7.6, 7.7, 7.8, 7.9, 8.0,
];

/**
 * Lê um número aceitando vírgula decimal (pt-BR) além do ponto.
 * @param {unknown} value
 * @returns {number} NaN quando não há número válido.
 */
function toNumber(value) {
  if (typeof value === 'number') return value;
  const raw = String(value ?? '').trim().replace(',', '.');
  if (raw === '') return NaN;
  return parseFloat(raw);
}

/**
 * @param {number} value
 * @returns {string} "1.234,56"
 */
function formatNumber(value) {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * @typedef {object} CalcState
 * @property {boolean} ok
 * @property {'invalid' | 'out_of_range'} [reason]
 * @property {number} [largura]
 * @property {number} [altura]
 * @property {number} [alturaFaixa]
 * @property {number} [faixas]
 * @property {number} [metros] metros lineares exatos (faixas × altura da faixa)
 * @property {number} [metrosCobrados] metros arredondados p/ cima — base do preço e da quantidade
 * @property {number} [totalVista] centavos
 * @property {number} [totalPrazo] centavos
 */

class RkArtCalculator extends RkComponent {
  static hydration = 'idle';

  setup() {
    const d = this.dataset;

    // As medidas da parede são digitadas em metros; as settings de largura da
    // faixa e sangria são em centímetros (é assim que o lojista pensa).
    this.faixaWidth = (toNumber(d.faixaWidth) || 60) / 100;
    this.sangria = (toNumber(d.sangria) || 0) / 100;
    this.alturaPar = toNumber(d.alturaPar) || 0;
    this.unitLabel = d.unit || 'm';

    // Preço unitário em centavos, vindo da variante selecionada.
    this.unitPrice = parseInt(d.unitPrice || '0', 10) || 0;
    this.variantPrices = this.#parseVariantPrices(d.variantPrices);
    this.vistaPct = parseInt(d.vistaPct || '0', 10) || 0;
    this.moneyFormat = d.moneyFormat || '${{amount}}';
    this.currency = d.currency || 'BRL';
    this.alturas = this.#parseAlturas(d.alturas);

    this.sendProperties = d.sendProperties !== 'false';
    this.hideQty = d.hideQuantitySelector !== 'false';

    this.lineTemplates = [d.resultLineOne || '', d.resultLineTwo || ''];
    this.invalidMessage = d.invalidMessage || 'Por favor, insira valores válidos.';
    this.outOfRangeMessage =
      d.outOfRangeMessage || 'Não temos faixa para essa altura de parede.';
    this.requireFieldsMessage =
      d.requireFieldsMessage || 'Informe a largura e a altura da parede para continuar.';

    /** Último cálculo válido exibido — permite refazer o texto na troca de variante. @type {CalcState | null} */
    this.lastState = null;

    this.widthInput = this.querySelector('[data-role="width"]');
    this.heightInput = this.querySelector('[data-role="height"]');
    this.resultEl = this.querySelector('[data-role="result"]');
    this.errorEl = this.querySelector('[data-role="error"]');

    this.applyQuantitySelectorState();
    this.associateInputsWithForm();

    this.delegate({
      'click [data-role="calculate"]': this.onCalculate,
      'keydown [data-role="width"]': this.onKeydown,
      'keydown [data-role="height"]': this.onKeydown,
      'input [data-role="width"]': this.onInput,
      'input [data-role="height"]': this.onInput,
      'invalid [data-role="width"]': this.onFieldInvalid,
      'invalid [data-role="height"]': this.onFieldInvalid,
    });

    // Reforço no submit do FORM que está sendo enviado (captura → roda antes
    // do handleSubmit do tema, que lê new FormData(form)).
    this.onFormSubmit = this.onFormSubmit.bind(this);
    document.addEventListener('submit', this.onFormSubmit, true);

    // A troca de variante re-renderiza o product-form: os inputs injetados e o
    // estado do seletor nativo se perdem, então reaplicamos.
    this.onVariantUpdate = this.onVariantUpdate.bind(this);
    document.addEventListener('variant:update', this.onVariantUpdate);

    this.syncForms();
  }

  teardown() {
    document.removeEventListener('submit', this.onFormSubmit, true);
    document.removeEventListener('variant:update', this.onVariantUpdate);
    document.querySelectorAll('input[data-rk-art]').forEach((el) => el.remove());
    document.querySelectorAll('.' + HIDDEN_CLASS).forEach((el) => el.classList.remove(HIDDEN_CLASS));
    for (const input of [this.widthInput, this.heightInput]) {
      if (!input) continue;
      input.removeAttribute('form');
      input.required = false;
    }
    delete document.documentElement.dataset.rkFaixas;
    delete document.documentElement.dataset.rkMetros;
  }

  /**
   * Aceita a tabela separada por vírgula, ponto-e-vírgula, espaço ou quebra de
   * linha. O separador decimal é o PONTO — a vírgula já é separador de itens.
   * @param {string | undefined} raw
   * @returns {number[]} ordenada crescente
   */
  #parseAlturas(raw) {
    const parsed = String(raw ?? '')
      .split(/[\s,;]+/)
      .map((part) => parseFloat(part))
      .filter((n) => Number.isFinite(n) && n > 0);

    if (parsed.length === 0) return ALTURAS_PADRAO.slice();
    return parsed.sort((a, b) => a - b);
  }

  /**
   * "id:preco,id:preco" → Map. Serve para reler o preço na troca de variante
   * sem depender de um re-render do bloco.
   * @param {string | undefined} raw
   * @returns {Map<string, number>}
   */
  #parseVariantPrices(raw) {
    const map = new Map();
    for (const pair of String(raw ?? '').split(',')) {
      const [id, price] = pair.split(':');
      const cents = parseInt(price, 10);
      if (id && Number.isFinite(cents)) map.set(id.trim(), cents);
    }
    return map;
  }

  /** @param {number} cents */
  money(cents) {
    return formatMoney(cents, this.moneyFormat, this.currency);
  }

  /* ── Cálculo ─────────────────────────────────────────────────────────── */

  /** @returns {CalcState} */
  #compute() {
    const largura = toNumber(this.widthInput?.value);
    const altura = toNumber(this.heightInput?.value);

    if (!Number.isFinite(largura) || !Number.isFinite(altura) || largura <= 0 || altura <= 0) {
      return { ok: false, reason: 'invalid' };
    }

    // Menor altura de faixa que cobre a parede + sangria.
    const alturaFaixa = this.alturas.find((alt) => alt >= altura + this.sangria);
    if (alturaFaixa === undefined) return { ok: false, reason: 'out_of_range' };

    let faixas = Math.max(1, Math.ceil(largura / this.faixaWidth));

    // Faixas altas saem de rolos que são cortados aos pares.
    if (this.alturaPar > 0 && alturaFaixa >= this.alturaPar && faixas % 2 !== 0) {
      faixas += 1;
    }

    // Em centímetros inteiros: `faixas * 2.7` daria 16,199999… e o Math.ceil
    // abaixo cobraria um metro a mais por puro resíduo binário.
    const metrosCm = faixas * Math.round(alturaFaixa * 100);
    const metros = metrosCm / 100;

    // O Shopify só aceita quantidade inteira, então o metro linear é vendido
    // fechado: 16,20 m é cobrado como 17 m. O mesmo número vai para a tela e
    // para o carrinho — o cliente nunca vê um valor diferente do que paga.
    const metrosCobrados = Math.ceil(metros);

    const totalPrazo = this.unitPrice * metrosCobrados;
    const totalVista = Math.floor((totalPrazo * (100 - this.vistaPct)) / 100);

    return {
      ok: true,
      largura,
      altura,
      alturaFaixa,
      faixas,
      metros,
      metrosCobrados,
      totalVista,
      totalPrazo,
    };
  }

  /* ── Interação ───────────────────────────────────────────────────────── */

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.onCalculate();
  }

  /**
   * Digitar só limpa o aviso anterior. O preço da página NÃO se mexe até o
   * clique em "Calcular" — quem garante que o carrinho não sai defasado é o
   * onFormSubmit, que recalcula e aplica no instante do envio.
   */
  onInput() {
    this.#clearError();
  }

  onFieldInvalid() {
    this.#showError(this.requireFieldsMessage);
  }

  onCalculate() {
    const state = this.#compute();

    if (!state.ok) {
      this.#showError(
        state.reason === 'out_of_range' ? this.outOfRangeMessage : this.invalidMessage
      );
      this.#clearResult();
      this.syncForms(state);
      return;
    }

    this.#clearError();
    this.lastState = state;
    this.#renderResult(state);
    this.syncForms(state);
  }

  /** @param {Event & { detail?: { resource?: { id?: string } } }} event */
  onVariantUpdate(event) {
    this.applyQuantitySelectorState();
    this.associateInputsWithForm();

    // O preço mudou junto com a variante. `data-unit-price` foi renderizado
    // com a variante inicial, então relemos pelo id da variante corrente.
    const id =
      event?.detail?.resource?.id ??
      document.querySelector('product-form-component form input[name="id"]')?.value;
    const price = this.variantPrices.get(String(id));
    if (Number.isFinite(price)) this.unitPrice = price;

    if (this.lastState) {
      const state = this.#compute();
      if (state.ok) {
        this.lastState = state;
        this.#renderResult(state);
      }
    }

    this.syncForms();
  }

  /* ── Resultado ───────────────────────────────────────────────────────── */

  /**
   * Monta as linhas do resultado a partir dos templates das settings. Os
   * valores entram como nós de texto dentro de <strong>, então nada do que o
   * lojista escrever nas settings é interpretado como HTML.
   * @param {CalcState} state
   */
  #renderResult(state) {
    if (!this.resultEl) return;

    const values = {
      faixas: String(state.faixas),
      altura: formatNumber(state.alturaFaixa),
      metros: String(state.metrosCobrados),
      vista: this.money(state.totalVista),
      prazo: this.money(state.totalPrazo),
    };

    this.resultEl.textContent = '';
    let first = true;

    for (const template of this.lineTemplates) {
      // Fora de uma página de produto não há preço: some com a linha em vez
      // de anunciar R$ 0,00.
      if (!template) continue;
      if (this.unitPrice <= 0 && /\{(vista|prazo)\}/.test(template)) continue;

      if (!first) this.resultEl.appendChild(document.createElement('br'));
      this.resultEl.appendChild(this.#buildLine(template, values));
      first = false;
    }

    this.resultEl.hidden = first;
  }

  /**
   * @param {string} template
   * @param {Record<string, string>} values
   * @returns {DocumentFragment}
   */
  #buildLine(template, values) {
    const fragment = document.createDocumentFragment();

    for (const part of template.split(/(\{[a-zA-Z_]+\})/g)) {
      if (part === '') continue;

      const key = part.startsWith('{') && part.endsWith('}') ? part.slice(1, -1) : null;

      if (key !== null && Object.hasOwn(values, key)) {
        const strong = document.createElement('strong');
        strong.className = 'rk-art-calc__strong';
        strong.textContent = values[key];
        fragment.appendChild(strong);
      } else {
        fragment.appendChild(document.createTextNode(part));
      }
    }

    return fragment;
  }

  #clearResult() {
    this.lastState = null;
    if (!this.resultEl) return;
    this.resultEl.textContent = '';
    this.resultEl.hidden = true;
  }

  /** @param {string} message */
  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  #clearError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.hidden = true;
  }

  /* ── Carrinho ────────────────────────────────────────────────────────── */

  /** @returns {HTMLFormElement[]} */
  #allForms() {
    const forms = [];
    document.querySelectorAll('product-form-component').forEach((pf) => {
      if (isIgnored(pf)) return;
      const form = pf.querySelector('form');
      if (form) forms.push(form);
    });
    return forms;
  }

  /**
   * Esconde o seletor nativo por classe. O atributo `hidden` não serve: o tema
   * declara `.quantity-selector-wrapper { display: flex }`, que vence a regra
   * `[hidden] { display: none }` da folha do navegador.
   */
  applyQuantitySelectorState() {
    if (!this.hideQty) return;
    document.querySelectorAll('product-form-component').forEach((pf) => {
      if (isIgnored(pf)) return;
      pf.querySelectorAll('.quantity-selector-wrapper, .quantity-label').forEach((el) => {
        el.classList.add(HIDDEN_CLASS);
      });
    });
  }

  /**
   * Quando a calculadora é a única fonte de quantidade, largura E altura
   * precisam ser obrigatórias — a altura entra na conta pela regra do nº par.
   *
   * Interceptar o submit não resolve sozinho: o tema registra o listener de
   * `submit` em document/capture antes deste script, então stopPropagation()
   * chega tarde. Já a validação nativa é respeitada em dois pontos do tema
   * (`handleClick` faz `form.checkValidity()`) e pelo próprio navegador, que
   * nem dispara `submit` num form inválido.
   *
   * Os inputs ficam fora do <form>, então associamos pelo atributo `form`.
   * Eles não têm `name`, logo continuam fora do payload.
   */
  associateInputsWithForm() {
    if (!this.hideQty) return;
    const form = [...document.querySelectorAll('product-form-component form')].find(
      (candidate) => !isIgnored(candidate)
    );
    // Atenção: `form.id` NÃO devolve o id aqui. O form tem <input name="id">
    // (o id da variante) e propriedades nomeadas de formulário viram
    // propriedades próprias, sombreando `Element.prototype.id`.
    const formId = form?.getAttribute('id');
    if (!formId) return;

    for (const input of [this.widthInput, this.heightInput]) {
      if (!input) continue;
      input.setAttribute('form', formId);
      input.required = true;
    }
  }

  /**
   * Propaga o estado atual para todos os product-forms da página.
   * @param {CalcState} [state] recalculado quando omitido
   */
  syncForms(state) {
    const current = state ?? this.#compute();
    const faixas = current.ok ? current.faixas : 0;

    // Publica antes de emitir: um bloco de preço que hidrate depois do evento
    // ainda consegue ler o estado corrente.
    document.documentElement.dataset.rkFaixas = String(faixas);
    document.documentElement.dataset.rkMetros = current.ok ? String(current.metrosCobrados) : '0';

    this.#allForms().forEach((form) => this.#applyToForm(form, current));

    this.rkEmit('faixa:update', {
      faixas,
      alturaFaixa: current.alturaFaixa ?? 0,
      metros: current.metrosCobrados ?? 0,
      metrosExatos: current.metros ?? 0,
      totalVista: current.totalVista ?? 0,
      totalPrazo: current.totalPrazo ?? 0,
    });
  }

  /** @param {Event} event */
  onFormSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.closest('product-form-component')) return;
    // Submit de outro produto (ex.: amostra): a calculadora não opina.
    if (isIgnored(form)) return;

    const state = this.#compute();

    // Sem medidas válidas não há quantidade a enviar. A validação nativa já
    // barra o submit antes daqui; isto é só a rede de segurança.
    if (!state.ok) {
      if (!this.hideQty) return;
      event.preventDefault();
      this.#showError(
        state.reason === 'out_of_range' ? this.outOfRangeMessage : this.requireFieldsMessage
      );
      const target = toNumber(this.widthInput?.value) > 0 ? this.heightInput : this.widthInput;
      target?.focus();
      return;
    }

    this.#applyToForm(form, state);
  }

  /**
   * @param {HTMLFormElement} form
   * @param {CalcState} state
   */
  #applyToForm(form, state) {
    if (!state.ok) {
      form.querySelectorAll('input[data-rk-art]').forEach((el) => el.remove());
      return;
    }

    let qty = form.querySelector('input[name="quantity"]');
    if (!qty) {
      qty = document.createElement('input');
      qty.type = 'hidden';
      qty.name = 'quantity';
      qty.setAttribute('data-rk-art', '');
      form.appendChild(qty);
    }
    // O produto é vendido por metro linear: a quantidade É o nº de metros.
    qty.value = String(state.metrosCobrados);

    if (this.sendProperties) {
      this.#prop(form, 'Largura', `${formatNumber(state.largura)} ${this.unitLabel}`);
      this.#prop(form, 'Altura', `${formatNumber(state.altura)} ${this.unitLabel}`);
      this.#prop(form, 'Faixas', String(state.faixas));
      // A quantidade não diz mais o tamanho do corte, então ele vai explícito.
      this.#prop(form, 'Altura da faixa', `${formatNumber(state.alturaFaixa)} ${this.unitLabel}`);
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} name
   * @param {string} value
   */
  #prop(form, name, value) {
    const selector = `input[name="properties[${name}]"][data-rk-art]`;
    let input = form.querySelector(selector);

    if (value === '') {
      if (input) input.remove();
      return;
    }

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.setAttribute('data-rk-art', '');
      form.appendChild(input);
    }
    input.value = value;
  }
}

if (!customElements.get('rk-art-calculator-element')) {
  customElements.define('rk-art-calculator-element', RkArtCalculator);
}
